// Owns the only PostHog client (PostHog client spec §3-§4, ADR 003). iOS only: disabled
// off-native, under NODE_ENV=test and without REACT_APP_POSTHOG_KEY. Every export is total
// -- it never throws into a caller and returns undefined -- and failures go to the
// injected onInternalError (the onboarding gate passes errorReporting.reportSentryOnly). This
// module never imports errorReporting.js or platform.js (dependency rule, §3); both are
// injected through initAnalytics(). No register() during init -- app_version/platform are
// stamped per event by scrub (§9 step 5); the only other init-time SDK call clears a
// persisted opt-out left by an earlier failed launch.
import posthog from 'posthog-js/full/no-external';
import { scrub } from './analytics-scrub';
import { APP_VERSION } from './constants';

let initialized = false;
let enabled = false;
// Onboarding gate pause (spec D9). Kept across init so a paused gate starts the SDK paused.
let allowed = true;
let onInternalError = () => {};

function reportInternal(err) {
  try {
    onInternalError(err);
  } catch {
    // A throwing reporter is swallowed (F5): analytics must never break the app.
  }
}

// before_send for every SDK event, $exception and $snapshot included (§4 step 14).
export function safeScrub(event) {
  try {
    return scrub(event, { appVersion: APP_VERSION });
  } catch (err) {
    reportInternal(err);
    return null;
  }
}

// §6, exact. `defaults` is deliberately unset: with defaults >= 2026-01-30 the
// capacitor://localhost host marks every tester as internal. Built fresh per init and
// never frozen: posthog-js _init writes into the object it is handed (config.debug,
// posthog-core.js:617), so a frozen config throws on every device (pre-Codex P1).
export function posthogConfig() {
  return {
    api_host: 'https://us.i.posthog.com',
    persistence: 'localStorage',
    person_profiles: 'identified_only',
    disable_external_dependency_loading: true,
    advanced_disable_feature_flags: true,
    autocapture: false,
    rageclick: false,
    capture_pageview: false,
    capture_pageleave: false,
    capture_dead_clicks: false,
    enable_heatmaps: false,
    capture_performance: false,
    mask_all_text: true,
    mask_all_element_attributes: true,
    mask_personal_data_properties: true,
    disable_capture_url_hashes: true,
    capture_exceptions: { capture_unhandled_errors: true, capture_unhandled_rejections: true, capture_console_errors: false },
    disable_surveys: true,
    disable_conversations: true,
    disable_product_tours: true,
    enable_recording_console_log: false,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: '*',
      maskAllElementAttributes: true,
      blockSelector: 'img',
      recordHeaders: false,
      recordBody: false,
      maskCapturedNetworkRequestFn: () => null,
    },
    before_send: safeScrub,
  };
}

export function initAnalytics(deps = {}) {
  try {
    if (typeof deps.onInternalError === 'function') onInternalError = deps.onInternalError;
    if (initialized) return undefined;
    if (process.env.NODE_ENV === 'test') return undefined;
    if (!deps.isNative?.()) return undefined;
    const key = process.env.REACT_APP_POSTHOG_KEY;
    if (!key) return undefined;
    initialized = true;
    try {
      posthog.init(key, posthogConfig());
      enabled = true;
    } catch (err) {
      // F2: a partial init may have installed capture or replay machinery; stop it.
      try { posthog.opt_out_capturing(); } catch { /* best effort */ }
      try { posthog.stopSessionRecording(); } catch { /* best effort */ }
      reportInternal(err);
      return undefined;
    }
    // The F2 opt-out above is persisted by posthog-js for 365 days, not the session. The
    // app never opts out on purpose, so a successful init clears any saved opt-out
    // (pre-Codex P2; Nicky 2026-10-02). A failure here leaves the facade enabled.
    try {
      if (posthog.has_opted_out_capturing()) posthog.clear_opt_in_out_capturing();
    } catch (err) {
      reportInternal(err);
    }
  } catch (err) {
    reportInternal(err);
  }
  return undefined;
}

// Disabled -> untouched SDK; enabled -> the call, with any throw contained (F4). Paused (D9) ->
// no-op, except resetUser, which captures nothing and clears identity.
function guarded(call, { whilePaused = false } = {}) {
  return (...args) => {
    if (!enabled || (!allowed && !whilePaused)) return undefined;
    try {
      call(...args);
    } catch (err) {
      reportInternal(err);
    }
    return undefined;
  };
}

export const identifyUser = guarded((userId) => posthog.identify(userId));
// Pause the SDK (spec D9): opt out of capture, stop exception autocapture, stop replay. Each step runs
// on its own, so one throwing never skips the others (fail closed); every throw is reported. Exception
// autocapture is stopped separately because a throwing opt-out would leave its observer posting.
function pauseSdk() {
  try { posthog.opt_out_capturing(); } catch (err) { reportInternal(err); }
  try { posthog.stopExceptionAutocapture(); } catch (err) { reportInternal(err); }
  try { posthog.stopSessionRecording(); } catch (err) { reportInternal(err); }
}

// Resume after a pause: opt back in (no $opt_in round trip; scrub drops it anyway) and restore exception
// autocapture with exactly the configured scope (never console errors). Independent steps, as above.
function resumeSdk() {
  try { posthog.opt_in_capturing({ captureEventName: false }); } catch (err) { reportInternal(err); }
  try { posthog.startExceptionAutocapture(posthogConfig().capture_exceptions); } catch (err) { reportInternal(err); }
}

// posthog-js reset() also clears stored consent (posthog-core _reset -> consent.reset), which would
// silently undo a pause and re-enable the SDK's exception autocapture; re-apply the pause, even when
// reset() throws partway (guarded still reports the throw).
export const resetUser = guarded(() => {
  try {
    posthog.reset();
  } finally {
    if (!allowed) pauseSdk();
  }
}, { whilePaused: true });
export const track = guarded((name, props) => posthog.capture(name, props));

// #1438 F1: sign_in_completed is held for the signing-in uid until the onboarding gate resolves that
// account. useAuth holds it (a newer sign-in replaces it); the gate releases it at `app` (captured
// through `track`, so a paused SDK still drops it) or drops it on a block or an account error. Total:
// plain assignments and the guarded `track`.
let pendingSignIn = null;
export function holdSignIn(userId) {
  pendingSignIn = typeof userId === 'string' && userId ? userId : null;
  return undefined;
}
export function releaseSignIn(userId) {
  if (pendingSignIn === null || pendingSignIn !== userId) return undefined;
  pendingSignIn = null;
  track('sign_in_completed'); // C10: no properties
  return undefined;
}
export function dropSignIn() {
  pendingSignIn = null;
  return undefined;
}
export const captureError = guarded((err, props) => posthog.captureException(err, props));

// Onboarding gate (spec D9): pause/resume capture for the current user. Total; SDK calls only when
// initialized. Pausing opts out and stops exception autocapture and replay (pauseSdk).
export function setAnalyticsAllowed(next) {
  try {
    allowed = next === true;
    if (!enabled) return undefined;
    if (allowed) {
      resumeSdk();
    } else {
      pauseSdk();
    }
  } catch (err) {
    reportInternal(err);
  }
  return undefined;
}
